import {
  pageEvent,
  trackEvent,
  getCommonEventProperties,
  waitForAnalyticsLoad,
} from "@acko-tech/building-blocks.ui.acko-analytics";
import {
  getCurrentPageType,
  getExternalReferrer,
  segmentEventCommonProp,
} from "./utils";
import { waitForAnalyticsReady } from "./analyticsReadiness";
import { getCookie, setCookie } from "cookies-next";
import { jwtDecode } from "jwt-decode";

const USER_ID_COOKIE_MAX_AGE = 14 * 24 * 60 * 60; // 14 days

function decodeJwtPayload(token: string): { sub?: string; exp?: number } | null {
  try {
    return jwtDecode<{ sub?: string; exp?: number }>(token);
  } catch {
    return null;
  }
}

function isJwtPayloadExpired(payload: { exp?: number } | null): boolean {
  if (!payload) return true;
  if (payload.exp == null) return false;
  return payload.exp < Math.floor(Date.now() / 1000);
}

export enum EventType {
  PAGE_EVENT = "page",
  TRACK_EVENT = "track",
  IDENTIFY_EVENT = "identify",
}
export interface EventTrackerOptions {
  pageType?: string;
  additionalProps?: Record<string, any>;
  customWriteKey?: string;
}

export const trackEventWithCommonProps = async (
  eventName: string,
  eventType: EventType = EventType.TRACK_EVENT,
  options: EventTrackerOptions = {}
): Promise<void> => {
  try {
    const { pageType, additionalProps = {}, customWriteKey } = options;

    await waitForAnalyticsLoad();

    const isAnalyticsReady = await waitForAnalyticsReady();
    if (!isAnalyticsReady) {
      return;
    }

    const commonProps = segmentEventCommonProp();
    const ackoCommonProps = getCommonEventProperties(
      pageType || getCurrentPageType()
    );

    let source = "";
    if (typeof window !== "undefined") {
      const storedSource = sessionStorage.getItem("referrerSource");

      if (storedSource) {
        source = storedSource;
      } else {
        const currentSource = getExternalReferrer();
        if (
          currentSource &&
          !currentSource.includes("ackodrive.com") &&
          !currentSource.includes("acko.com")
        ) {
          source = currentSource;
          sessionStorage.setItem("referrerSource", currentSource);
        }
      }
    }

    const eventProperties = {
      ...Object.fromEntries(
        Object.entries(ackoCommonProps).filter(([_, v]) => v != "")
      ),
      ...Object.fromEntries(
        Object.entries(commonProps).filter(([_, v]) => v !== "")
      ),
      ...additionalProps,
    };

    // Manually override source - always set it
    (eventProperties as any)["source"] = source || "";

    // Segment `user_id` = Keycloak `sub`; `user_id` cookie = access JWT (backend validates cookie)
    if (typeof window !== "undefined") {
      let analyticsUserId: string | null = null;

      const ackoDriveTokens = getCookie("ackodrive_tokens");
      if (ackoDriveTokens) {
        try {
          const tokensObj = JSON.parse(String(ackoDriveTokens));
          const accessToken = tokensObj?.access;
          if (accessToken) {
            const accessStr = String(accessToken);
            const payload = decodeJwtPayload(accessStr);
            if (payload && !isJwtPayloadExpired(payload) && payload.sub) {
              analyticsUserId = payload.sub;
              setCookie("user_id", accessStr, {
                path: "/",
                maxAge: USER_ID_COOKIE_MAX_AGE,
              });
            }
          }
        } catch (error) {
          console.error("Error parsing ackodrive_tokens:", error);
        }
      }

      if (!analyticsUserId) {
        const rawCookie = getCookie("user_id");
        if (rawCookie) {
          const rawStr = String(rawCookie);
          const parts = rawStr.split(".");
          if (parts.length === 3) {
            const payload = decodeJwtPayload(rawStr);
            if (payload && !isJwtPayloadExpired(payload) && payload.sub) {
              analyticsUserId = payload.sub;
            }
          } else {
            const trimmed = rawStr.trim();
            if (trimmed) {
              analyticsUserId = trimmed;
            }
          }
        }
      }

      (eventProperties as any)["user_id"] = analyticsUserId || null;
    }

    switch (eventType) {
      case EventType.PAGE_EVENT:
        await pageEvent(eventName, eventProperties, customWriteKey);
        break;
      case EventType.TRACK_EVENT:
        await trackEvent(eventName, eventProperties, customWriteKey);
        break;
      case EventType.IDENTIFY_EVENT:
        await trackEvent(eventName, eventProperties, customWriteKey);
        break;
      default:
        console.error(`Invalid event type: ${eventType}`);
        break;
    }
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};
